'use client'

import { CodeBlock } from '@brew/code-block'
import { Button } from '@brew/ui/button'
import { Input } from '@brew/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@brew/ui/select'
import { Check, Copy } from 'lucide-react'
import { useCallback, useState } from 'react'
import type { IntegrationMeta } from '@/lib/integrations/catalog'
import { appToast } from '@/lib/shared/toast'
import type { ProviderSettings } from '../config/provider-configs'

type TutorialStep = NonNullable<IntegrationMeta['tutorialSteps']>[number]

interface TutorialStepsProps {
  steps: Array<TutorialStep>
  settings: ProviderSettings
  onSettingsChange: (partial: Partial<ProviderSettings>) => void
}

/**
 * Numbered prose-style step list rendered inside the integration
 * detail page (Attio-style minimal form). Each step is a row:
 * `(N)` index in a small circle, then a title + description, then any
 * required input field directly underneath. No cream cards, no
 * brand-warm tokens — the page sits on `bg-background` and the form
 * inputs use the standard shadcn input styling.
 *
 * Supports three input variants:
 *  - `text` / `password` / `email` (default `<Input>`)
 *  - `select` (a `<Select>` with the step's `options`)
 *  - `copy` (a read-only `<Input>` paired with a clipboard
 *    `<Button>`). Inbound providers use this for the generated
 *    `iac_<nanoid>` webhook URL that the user pastes into their
 *    provider's dashboard.
 */

function StepInput({
  step,
  settings,
  onSettingsChange,
}: {
  step: TutorialStep
  settings: ProviderSettings
  onSettingsChange: (partial: Partial<ProviderSettings>) => void
}) {
  const { input } = step
  const editableKey = input && input.type !== 'code' ? input.key : null

  const handleSelectChange = useCallback(
    (value: string) => {
      if (editableKey) {
        onSettingsChange({ [editableKey]: value })
      }
    },
    [editableKey, onSettingsChange]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (editableKey) {
        onSettingsChange({ [editableKey]: e.target.value })
      }
    },
    [editableKey, onSettingsChange]
  )

  if (!input) {
    return null
  }

  if (input.type === 'code') {
    const rendered = renderCodeValue(input.value, settings)
    const webhookUrl = settings.webhookUrl?.trim()
    const hasSensitiveWebhookUrl = Boolean(
      webhookUrl && rendered.includes(webhookUrl)
    )
    return (
      <CopyableCodeBlock
        value={rendered}
        language={input.language}
        canCopy={!hasUnresolvedRequiredToken(rendered)}
        isSensitive={hasSensitiveWebhookUrl}
      />
    )
  }

  if (input.type === 'copy') {
    const value = settings[input.key] ?? ''
    return <CopyableInput value={value} placeholder={input.placeholder} />
  }

  if (input.type === 'select' && input.options) {
    const currentValue = settings[input.key] ?? input.options[0]?.value

    return (
      <Select value={currentValue} onValueChange={handleSelectChange}>
        <SelectTrigger className="mt-3 h-9 w-full text-sm">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent collisionPadding={16}>
          {input.options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <Input
      type={input.type}
      name={`integration-${input.key}`}
      value={settings[input.key] ?? ''}
      onChange={handleInputChange}
      placeholder={input.placeholder}
      autoComplete={input.type === 'password' ? 'new-password' : 'off'}
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      data-lpignore="true"
      data-1p-ignore="true"
      data-dd-privacy={input.type === 'password' ? 'hidden' : undefined}
      className="mt-3 h-9 text-sm"
    />
  )
}

function CopyableInput({
  value,
  placeholder,
}: {
  value: string
  placeholder?: string
}) {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    if (!value) {
      return
    }
    try {
      await navigator.clipboard.writeText(value)
      setIsCopied(true)
      appToast.success('Copied to clipboard')
      setTimeout(() => setIsCopied(false), 2000)
    } catch {
      appToast.error('Could not copy. Select the URL and copy manually.')
    }
  }, [value])

  return (
    <div className="mt-3 flex gap-2" data-dd-privacy="hidden">
      <Input
        type="text"
        value={value}
        readOnly
        placeholder={placeholder}
        data-dd-privacy="hidden"
        className="h-9 min-w-0 flex-1 font-mono text-xs"
        onFocus={(e) => e.currentTarget.select()}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleCopy}
        disabled={!value}
        aria-label="Copy"
        title="Copy to clipboard"
        className="h-9 w-9"
      >
        {isCopied ? (
          <Check className="size-3.5" />
        ) : (
          <Copy className="size-3.5" />
        )}
      </Button>
    </div>
  )
}

/**
 * Renders the static `value` field of a `'code'` input, replacing
 * supported `{{token}}` markers with live values from the connect
 * dialog's `settings`. Today only `{{webhookUrl}}` is wired so the
 * operator sees their minted URL inside the SQL/script block — the
 * other placeholders (e.g. `<PASTE_YOUR_SECRET_HERE>`) stay literal
 * so the operator knows where to splice their own input.
 *
 * Kept tiny on purpose: every new token broadens the surface area
 * for typos in catalog `value` strings, so only add one when a real
 * provider step needs it.
 */
function renderCodeValue(value: string, settings: ProviderSettings): string {
  const webhookUrl = settings.webhookUrl?.trim()
  if (!webhookUrl) {
    return value
  }
  return value.replace(/\{\{webhookUrl\}\}/g, webhookUrl)
}

function hasUnresolvedRequiredToken(value: string): boolean {
  return /\{\{webhookUrl\}\}/.test(value)
}

function resolveTutorialCodeLanguage(language?: string) {
  switch (language) {
    case 'bash':
    case 'json':
    case 'python':
    case 'sql':
    case 'typescript':
      return language
    default:
      return 'text'
  }
}

function CopyableCodeBlock({
  value,
  language,
  canCopy,
  isSensitive,
}: {
  value: string
  language?: string
  canCopy: boolean
  isSensitive: boolean
}) {
  const handleCopy = useCallback(() => {
    appToast.success('Code copied')
  }, [])
  const handleCopyError = useCallback(() => {
    appToast.error('Could not copy — select the block and copy it manually')
  }, [])

  return (
    <div className="mt-3" data-dd-privacy={isSensitive ? 'hidden' : undefined}>
      <CodeBlock
        code={value}
        language={resolveTutorialCodeLanguage(language)}
        canCopy={Boolean(value) && canCopy}
        copyLabel="Copy code"
        onCopy={handleCopy}
        onCopyError={handleCopyError}
        viewportClassName="max-h-[420px]"
      />
    </div>
  )
}

export function TutorialSteps({
  steps,
  settings,
  onSettingsChange,
}: TutorialStepsProps) {
  return (
    <ol className="flex flex-col gap-5">
      {steps.map((step, index) => (
        <li key={step.label} className="flex gap-3">
          <span
            className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-lg border border-border font-medium text-caption text-foreground/70"
            aria-hidden
          >
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-foreground/90 text-sm leading-relaxed">
              <span className="font-medium">{step.label}.</span>{' '}
              <span className="text-muted-foreground">{step.description}</span>
            </p>
            <StepInput
              step={step}
              settings={settings}
              onSettingsChange={onSettingsChange}
            />
          </div>
        </li>
      ))}
    </ol>
  )
}
